/* ----------------------------------------------------------------------------
   SEARCH DRAWER — enhancement layer (sections/search-drawer.liquid)
   ----------------------------------------------------------------------------
   The drawer mechanics (<x-drawer>: Esc, overlay, slide animation, scroll-lock,
   focus-return) come from theme.js. Live results come from the native
   <predictive-search> inside the drawer (Search & Discovery applies).
   This element adds behaviour on top:

   - toggles [data-state="default|results"] on itself as the user types.
   - forces focus into the search field when the drawer opens, mobile included.
   - manages Tab focus inside the drawer (see FOCUS ORDER below).
   - "direct to collection": pressing Enter (or the "view all" submit) on an exact
     product-type term (or its curated typo) jumps straight to that collection.
   - "Meintest du …?": when a query returns no products, suggests the nearest
     product-type term (curated typo table + umlaut/accent-folded Levenshtein).

   Market data (term→collection, typo table, "did you mean" label) comes from
   snippets/search-config.liquid (#search-experience-config). This file stays
   language-agnostic: no copy lives here.

   FOCUS ORDER — why we override the theme's focus-trap for Tab:
   The <x-drawer> trap's tabbable walk treats custom elements as shadow hosts and
   skips the LIGHT children of <search-experience>/<product-list>/<product-card>,
   so Tab only reached the field and the × button. We run our own deterministic
   roving-Tab handler in capture, registered before the trap activates, and
   stopImmediatePropagation to own Tab while the drawer is open. Order: field →
   focusables in the active view → × close button → wrap.
---------------------------------------------------------------------------- */
(() => {
  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  // Lowercase, strip accents (é→e, ä→a…) and ß→ss, collapse whitespace. Lets the
  // matcher treat "Nachtlicht", "nachtlicht" and "veilleuse"/"Veilleuse" alike and
  // makes German plurals fall within Levenshtein range of their singular.
  function fold(value) {
    return (value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ß/g, 'ss')
      .replace(/\s+/g, ' ')
      .trim();
  }

  // Levenshtein edit distance (iterative, single row).
  function editDistance(a, b) {
    const m = a.length;
    const n = b.length;
    if (m === 0) return n;
    if (n === 0) return m;
    const row = new Array(n + 1);
    for (let j = 0; j <= n; j += 1) row[j] = j;
    for (let i = 1; i <= m; i += 1) {
      let prev = row[0];
      row[0] = i;
      for (let j = 1; j <= n; j += 1) {
        const tmp = row[j];
        const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
        row[j] = Math.min(row[j] + 1, row[j - 1] + 1, prev + cost);
        prev = tmp;
      }
    }
    return row[n];
  }

  class SearchExperience extends HTMLElement {
    constructor() {
      super();
      this.onKeydown = this.onKeydown.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.updateSuggestion = this.updateSuggestion.bind(this);
    }

    connectedCallback() {
      this.drawer = this.closest('x-drawer');
      this.field = (this.drawer || document).querySelector('[data-search-field]');

      if (!this.field) {
        return;
      }

      this.loadConfig();

      // View toggle: default when empty, results while typing.
      this.field.addEventListener('input', () => this.syncView());
      this.syncView();

      // Direct-to-collection on submit (Enter / "view all results" button).
      this.form = (this.drawer || document).querySelector('#predictive-search-form');
      if (this.form) {
        this.form.addEventListener('submit', this.onSubmit);
      }

      // "Reset" text link clears the field (replaces the hidden native ×).
      const resetBtn = (this.drawer || document).querySelector('[data-search-reset]');
      if (resetBtn) {
        resetBtn.addEventListener('click', () => {
          this.field.value = '';
          this.field.dispatchEvent(new Event('input', { bubbles: true }));
          this.field.focus();
        });
      }

      // "Meintest du …?": watch the injected results and suggest on no-results.
      this.suggestEl = (this.drawer || document).querySelector('[data-suggest]');
      this.noResultsEl = (this.drawer || document).querySelector('[data-no-results-view]');
      this.resultsContent = (this.drawer || document).querySelector('.predictive-search__content');
      if (this.resultsContent) {
        this.resultsObserver = new MutationObserver(this.updateSuggestion);
        this.resultsObserver.observe(this.resultsContent, { childList: true, subtree: true });
        // <predictive-search> dispatches this after replaceChildren() on the results
        // slot — a reliable second trigger in case the observer batch is missed.
        document.documentElement.addEventListener('theme:loading:end', this.updateSuggestion);
      }

      if (this.drawer) {
        this.observer = new MutationObserver(() => {
          if (this.drawer.hasAttribute('open')) {
            this.focusField();
          }
        });
        this.observer.observe(this.drawer, { attributes: true, attributeFilter: ['open'] });
        if (this.drawer.hasAttribute('open')) {
          this.focusField();
        }

        document.addEventListener('keydown', this.onKeydown, true);
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.resultsObserver?.disconnect();
      this.form?.removeEventListener('submit', this.onSubmit);
      document.documentElement.removeEventListener('theme:loading:end', this.updateSuggestion);
      document.removeEventListener('keydown', this.onKeydown, true);
    }

    // ---- config / matching --------------------------------------------------

    loadConfig() {
      this.searchUrl = '/search';
      this.termToUrl = {}; // folded term → collection URL
      this.terms = []; // [{ folded, raw }] canonical product-type terms
      this.typo = {}; // folded typo → raw correct term
      this.didYouMean = '';

      let config = null;
      const el = document.getElementById('search-experience-config');
      if (el) {
        try { config = JSON.parse(el.textContent); } catch (e) { config = null; }
      }
      if (!config) return;

      this.searchUrl = config.searchUrl || '/search';
      this.didYouMean = config.didYouMean || '';

      const t2c = config.termToCollection || {};
      Object.keys(t2c).forEach((term) => {
        const folded = fold(term);
        this.termToUrl[folded] = t2c[term];
        this.terms.push({ folded, raw: term });
      });
      (config.extraTerms || []).forEach((term) => {
        const folded = fold(term);
        if (!this.terms.some((t) => t.folded === folded)) {
          this.terms.push({ folded, raw: term });
        }
      });
      const typo = config.typoCorrections || {};
      Object.keys(typo).forEach((key) => { this.typo[fold(key)] = typo[key]; });
    }

    // A collection URL for an exact term or its exact typo correction (no fuzzy —
    // navigation should only happen on high-confidence matches).
    collectionUrlFor(query) {
      const q = fold(query);
      if (!q) return null;
      if (this.termToUrl[q]) return this.termToUrl[q];
      if (this.typo[q]) {
        const corrected = fold(this.typo[q]);
        if (this.termToUrl[corrected]) return this.termToUrl[corrected];
      }
      return null;
    }

    // Nearest product-type term for a no-results query: exact typo table first,
    // then umlaut/accent-folded Levenshtein within a length-aware threshold.
    suggestionFor(query) {
      const q = fold(query);
      if (!q || this.terms.length === 0) return null;
      if (this.typo[q]) return this.typo[q];

      let best = null;
      let bestDistance = Infinity;
      this.terms.forEach((term) => {
        const distance = editDistance(q, term.folded);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = term;
        }
      });
      const threshold = q.length <= 4 ? 1 : 2;
      if (best && bestDistance > 0 && bestDistance <= threshold) {
        return best.raw;
      }
      return null;
    }

    // ---- behaviour ----------------------------------------------------------

    onSubmit(event) {
      const url = this.collectionUrlFor(this.field.value);
      if (url) {
        event.preventDefault();
        window.location.assign(url);
      }
      // else: let the form submit to /search as usual.
    }

    updateSuggestion() {
      const query = this.field.value.trim();
      const hasProducts = !!(this.resultsContent
        && this.resultsContent.querySelector('a[href*="/products/"]'));
      const noResults = query.length > 0 && !hasProducts;

      // No-results recovery (popular searches).
      if (this.noResultsEl) {
        this.noResultsEl.hidden = !noResults;
      }

      // "Meintest du …?" — on a no-results query, or a known misspelling (helpful even
      // if Search & Discovery returned fuzzy matches for it).
      if (this.suggestEl) {
        const suggestion = query ? this.suggestionFor(query) : null;
        const knownTypo = query && Object.prototype.hasOwnProperty.call(this.typo, fold(query));
        if (suggestion && (noResults || knownTypo)) {
          this.showSuggestion(suggestion);
        } else {
          this.hideSuggestion();
        }
      }
    }

    showSuggestion(term) {
      if (!this.suggestEl) return;
      this.suggestEl.textContent = '';
      if (this.didYouMean) {
        this.suggestEl.append(`${this.didYouMean} `);
      }
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = term;
      button.addEventListener('click', () => {
        this.field.value = term;
        this.field.dispatchEvent(new Event('input', { bubbles: true }));
        this.field.focus();
      });
      this.suggestEl.append(button);
      this.suggestEl.append('?');
      this.suggestEl.hidden = false;
    }

    hideSuggestion() {
      if (!this.suggestEl) return;
      this.suggestEl.hidden = true;
      this.suggestEl.textContent = '';
    }

    syncView() {
      const hasQuery = this.field.value.trim().length > 0;
      this.setAttribute('data-state', hasQuery ? 'results' : 'default');
      if (!hasQuery) {
        this.hideSuggestion();
        if (this.noResultsEl) {
          this.noResultsEl.hidden = true;
        }
      }
    }

    focusField() {
      requestAnimationFrame(() => {
        try {
          this.field.focus({ preventScroll: true });
        } catch (e) {
          this.field.focus();
        }
      });
    }

    // ---- roving Tab (see FOCUS ORDER above) ---------------------------------

    getFocusables() {
      const drawer = this.drawer;
      const list = [];
      drawer.querySelectorAll(FOCUSABLE).forEach((el) => {
        if (this.isVisible(el)) {
          list.push(el);
        }
      });
      const close = drawer.shadowRoot
        && drawer.shadowRoot.querySelector('[is="dialog-close-button"], [part~="close-button"]');
      if (close && this.isVisible(close)) {
        list.push(close);
      }
      return list;
    }

    isVisible(el) {
      return el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';
    }

    onKeydown(event) {
      if (event.key !== 'Tab') {
        return;
      }
      const drawer = this.drawer;
      if (!drawer || !drawer.hasAttribute('open')) {
        return;
      }

      const active = (drawer.shadowRoot && drawer.shadowRoot.activeElement) || document.activeElement;
      const insideDrawer = drawer.contains(document.activeElement)
        || (drawer.shadowRoot && drawer.shadowRoot.contains(active));
      if (!insideDrawer) {
        return;
      }

      const focusables = this.getFocusables();
      if (focusables.length < 2) {
        return;
      }

      let index = focusables.indexOf(active);
      if (index === -1) {
        index = 0;
      }
      let next = event.shiftKey ? index - 1 : index + 1;
      if (next < 0) {
        next = focusables.length - 1;
      } else if (next >= focusables.length) {
        next = 0;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      focusables[next].focus();
    }
  }

  if (!window.customElements.get('search-experience')) {
    window.customElements.define('search-experience', SearchExperience);
  }
})();
